import { getFormattedNumber } from 'src/components/ui/inputs/TextInput.utils'
import { $d, $t, $tc } from 'src/plugins/i18n'
import { getPortfolioConfiguration } from 'src/services/PortfolioApiStore'
import type { ProxyRecord } from 'src/services/deserializers/deserializers.types'
import { getEquivalentType, getField } from 'src/services/mappings/getMappings'
import type {
  ContextualFieldInterface,
  FieldInterface,
  GetEnums,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import type { ObjectType } from 'src/tpbp-configuration/models/models.const'
import {
  ENUM_SORTING_RULE,
  FIELD_DISPLAY_OPTIONS,
  FIELD_TYPES,
} from 'src/tpbp-core/models/fields/field-set.const'
import type { EnumInterface, Scalar } from 'src/tpbp-core/models/global.type'
import { isDevelopment, valueIsEmpty } from 'src/tpbp-core/utils/common.utils'
import type { PersonModel } from 'src/tpbp-json-api-handler/models/models'
import { getAge } from 'src/utils/getAge'
import _ from 'underscore'

export function getEnumLabelKey(objectType, fieldKey, key) {
  if (
    typeof key !== 'string' ||
    typeof objectType !== 'string' ||
    typeof fieldKey !== 'string' ||
    !key
  ) {
    return ''
  }
  return `models.${objectType}.enums.${fieldKey}.${key}`
}

function getDynamicEnumLabel(record, field, context = {}) {
  const helpers = {}
  return field.dynamicEnumLabel(record, helpers, field, context)
}

export function getEnumLabelFromRecord(record, fieldKey: string): string {
  const equivalentType = getEquivalentType(record.$type) // TODO is it useful ?
  const value = record[fieldKey]

  if (fieldKey === 'productKind') {
    const configuration = getPortfolioConfiguration(value)
    if (!configuration && isDevelopment) {
      throw new Error(`Unknown productKind '${value}'`)
    }
    return configuration?.productKindLabel || value
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const field = getField(equivalentType, fieldKey)!
  if (field.getEnumLabel) {
    return field.getEnumLabel(value, record)
  }
  const dynamicEnumLabel = field.dynamicEnumLabel
    ? getDynamicEnumLabel(record, field)
    : null

  if (dynamicEnumLabel) {
    return dynamicEnumLabel
  }

  const labelKey = getEnumLabelKey(equivalentType, fieldKey, value)

  return $t(labelKey)
}

// TODO rename to getEnumLabelFromValue
export function getEnumLabel(
  objectType: ObjectType,
  fieldKey: string,
  value: Scalar,
) {
  const record = {
    [fieldKey]: value,
    $type: objectType,
  }
  return getEnumLabelFromRecord(record, fieldKey)
}

export function getFieldEnums(
  record: ProxyRecord,
  fieldKey: string,
  enumKeys: FieldInterface['enumKeys'],
  getEnums?: GetEnums | undefined,
) {
  if (getEnums) return getEnums(record as unknown as Record<string, unknown>)
  if (!enumKeys) return
  const objectType = record.$type ?? ''

  const field = getField(objectType, fieldKey)
  if (field === undefined) {
    throw new Error(`Unknown field '${fieldKey}'`)
  }

  const fieldEnums = enumKeys.map<EnumInterface>((key) => {
    const label = field.getEnumLabel
      ? field.getEnumLabel(key, record as unknown as Record<string, unknown>)
      : getEnumLabel(objectType, fieldKey, key)
    const labelKey = field.dynamicEnumLabel
      ? '' // do not use labelKey when dynamicEnumLabel
      : getEnumLabelKey(objectType, fieldKey, key)

    return { key, label, labelKey }
  })

  return field.enumSortingRule === ENUM_SORTING_RULE.alphabeticalLabel
    ? _.sortBy(fieldEnums, 'label')
    : fieldEnums
}

function getEnumsValues<T extends Scalar>(objectType, field, values: T[]) {
  const displayValues = values.map((value) =>
    getEnumLabel(objectType, field.key, value),
  )

  return displayValues.join(', ')
}

export function getDisplayValue(
  objectType: ObjectType,
  record: ProxyRecord,
  field: ContextualFieldInterface,
) {
  const value = field.originRelationship
    ? record[field.originRelationship]
    : record[field.key]

  if (!objectType) {
    return value
  }

  // TODO find a more generic way to manage that
  if (field.key === 'financiallyDependentFrom') {
    return value ? `${value.firstName} ${value.lastName}` : ''
  }

  if (field.enums && field.isMultiple) {
    const equivalentType = getEquivalentType(objectType)

    return getEnumsValues(equivalentType, field, value)
  }
  if (field.enums) {
    return getEnumLabelFromRecord(record, field.key)
  }
  if (field.isMultiple) {
    return value.join(' ')
  }
  if (field.type === FIELD_TYPES.boolean) {
    const key = value ? 'common.Yes' : 'common.No'

    return $t(key)
  }
  if (field.type === FIELD_TYPES.date) {
    const displayValue = value ? $d(new Date(`${value}T00:00:00`)) : ''

    if (field.displayOption === FIELD_DISPLAY_OPTIONS.withAge) {
      const recordPerson = record as PersonModel
      const age = valueIsEmpty(recordPerson.age)
        ? getAge(recordPerson[field.key])
        : recordPerson.age

      return `${displayValue} (${$tc('common.n-year', age, { age })})`
    }

    return displayValue
  }
  if (field.type === FIELD_TYPES.number && field.format) {
    return getFormattedNumber(
      value as string | number | null | undefined,
      field.format,
    )
  }

  // not ideal, but we need to display the linked portfolio names
  if (field.key === 'linkedPortfolio') {
    return (value as { name: string }[]).map(({ name }) => name).join(', ')
  }

  return value as string | number | null | undefined
}
